import React, { useEffect, useState } from 'react';
import client from '../../../graphql/client';
import { useAuthState } from '../../auth/authContext';
import Monitor, { FingerPrint } from '../../lib/monitoring/Monitor';
import { Formik } from 'formik';
import { ScrollView } from 'react-native';
import LinearGradientButton from '../../components/styled/LinearGradientButton';
import Modal from '../../components/styled/Modal';
import SelectedGenresWidget from '../../components/styled/SelectedGenresWidget';
import TextInput from '../../components/styled/TextInput';
import Text from '../../components/styled/Text';
import UserError, { UserErrorCodes } from '../../components/styled/UserError';
import View from '../../components/styled/View';
import styles from './styles';
import { fanProfileSchema } from './validationSchema';
import { GENRE_SUB_TEXT, GENRE_TEXT, PROFILE_SAVED, TITLE } from './constants';
import {
  separatorLightColor,
  separatorDarkColor,
} from '../../constants/palette';

export default function FanProfileScreen({ navigation, route }) {
  const { cognitoId, email } = useAuthState();
  const [fan, setFanInfo] = useState<any>();
  const [fanError, setFanError] = useState<keyof typeof UserErrorCodes>(
    UserErrorCodes.NONE,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [favGenres, setFavGenres] = useState<any>([]);
  const [modalVisible, setModalVisible] = useState(false);

  const goToFanGenres = () => {
    navigation.navigate('FanGenres', {
      favoriteGenres: favGenres,
      isCreate: false,
    });
  };

  const getFanInfo = async () => {
    const fanInfo = await client.getFanData(cognitoId);
    if (!fanInfo) {
      const monitorData = {
        context: { user_id: cognitoId, user_type: 'fan', user_email: email },
        name: 'FanProfile',
        fingerprint: FingerPrint.fanAccount,
        message: 'Fan profile error',
      };
      const error =
        'Error in fanProfile page, there is no fan data for the user';
      Monitor.notify(error, monitorData);
      // FIXME: This if block may not be needed
      setFanError(UserErrorCodes.FAN_PROFILE_ERROR);
      setIsLoading(false);
    } else {
      // Current current favGenres object array
      const fanFavGenres = fanInfo?.favoriteGenres?.items.map((fanGenre) => {
        return {
          id: fanGenre.genre.id,
          genre: fanGenre.genre.superGenre,
          fgId: fanGenre.id,
        };
      });
      // @ts-ignore
      setFanInfo(fanInfo);
      setFavGenres(fanFavGenres || []);
      setFanError(UserErrorCodes.NONE);
      setIsLoading(false);
    }
  };

  const updateFanProfile = async (values) => {
    const { fullName, zipCode } = values;

    const fanDetails = {
      cognitoId,
      fullName,
      zipCode,
    };
    let updatedFan;
    if (!fan) {
      updatedFan = await client.createAFan(fanDetails);
    } else {
      updatedFan = await client.updateFanData(fanDetails);
    }

    setFanInfo(updatedFan);
    setModalVisible(true);
  };

  useEffect(() => {
    getFanInfo();
  }, [route?.params?.newGenres]);

  if (isLoading) return null;

  if (fanError !== UserErrorCodes.NONE) {
    return (
      <ScrollView contentContainerStyle={styles.errorContainer}>
        <UserError errorType={fanError} navigation={navigation} />
      </ScrollView>
    );
  }
  return (
    <ScrollView contentContainerStyle={styles.scrollViewContainer}>
      <Modal
        messageText={PROFILE_SAVED}
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
      />
      <View style={styles.container}>
        <Text style={styles.title}>{TITLE}</Text>
        <View
          style={styles.separator}
          lightColor={separatorLightColor}
          darkColor={separatorDarkColor}
        />
        <Formik
          initialValues={{
            fullName: fan?.fullName,
            zipCode: fan?.zipCode,
          }}
          onSubmit={(values) => updateFanProfile(values)}
          validationSchema={fanProfileSchema}
        >
          {({
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            isValid,
            touched,
            values,
          }) => (
            <>
              <View style={styles.bodyContainer}>
                <TextInput
                  autoCapitalize="words"
                  autoCompleteType="name"
                  autoCorrect
                  errorMessage={touched.fullName && errors.fullName}
                  keyboardType="default"
                  label="Full Name"
                  labelHidden
                  onBlur={handleBlur('fullName')}
                  onChangeText={handleChange('fullName')}
                  placeholder="Full Name"
                  value={values.fullName}
                />
                <TextInput
                  autoCompleteType="postal-code"
                  autoCapitalize="none"
                  errorMessage={touched.zipCode && errors.zipCode}
                  keyboardType="default"
                  label="Zip Code"
                  labelHidden
                  onBlur={handleBlur('zipCode')}
                  onChangeText={handleChange('zipCode')}
                  placeholder="Zip Code"
                  value={values.zipCode}
                />
                <SelectedGenresWidget
                  editButtonOnPress={goToFanGenres}
                  genreButtonOnPress={() => {}}
                  genres={favGenres}
                  isDefaultGenreButtonActive={true}
                  text={GENRE_TEXT}
                  subText={GENRE_SUB_TEXT}
                />
                <View
                  style={styles.bottomSeparator}
                  lightColor={separatorLightColor}
                  darkColor={separatorDarkColor}
                />
                <View style={styles.buttonContainer}>
                  <LinearGradientButton
                    disabled={!isValid}
                    title="Save"
                    onPress={handleSubmit}
                  />
                </View>
              </View>
            </>
          )}
        </Formik>
      </View>
    </ScrollView>
  );
}
